import { createContext, useContext } from 'react';

import { type HTMLArkProps, ark } from '@ark-ui/react';

import type { Radius } from '../../lib/types';
import { cn } from '../../lib/utils';

type TableSize = '1' | '2' | '3';
type TableVariant = 'surface' | 'ghost';

interface TableContextValue {
	size: TableSize;
	variant: TableVariant;
}

const TableContext = createContext<TableContextValue>({
	size: '2',
	variant: 'surface'
});

const useTableContext = () => useContext(TableContext);

const variantClasses: Record<TableVariant, string> = {
	surface: 'pt-table-surface',
	ghost: 'pt-table-ghost'
};

const headerVariantClasses: Record<TableVariant, string> = {
	surface: 'pt-table-header-surface',
	ghost: 'pt-table-header-ghost'
};

const rowVariantClasses: Record<TableVariant, string> = {
	surface: 'pt-table-row-surface',
	ghost: 'pt-table-row-ghost'
};

const headerSizeClasses: Record<TableSize, string> = {
	'1': 'pt-table-header-size-1',
	'2': 'pt-table-header-size-2',
	'3': 'pt-table-header-size-3'
};

const cellSizeClasses: Record<TableSize, string> = {
	'1': 'pt-table-cell-size-1',
	'2': 'pt-table-cell-size-2',
	'3': 'pt-table-cell-size-3'
};

interface TableRootProps extends HTMLArkProps<'table'> {
	size?: TableSize;
	variant?: TableVariant;
	radius?: Radius;
}

function TableRoot({
	className,
	size = '2',
	variant = 'surface',
	radius = 'medium',
	children,
	...props
}: TableRootProps) {
	return (
		<TableContext.Provider value={{ size, variant }}>
			<ark.table
				data-slot="table"
				data-radius={radius}
				className={cn('pt-table', variantClasses[variant], className)}
				{...props}
			>
				{children}
			</ark.table>
		</TableContext.Provider>
	);
}

function TableHeader({ className, ...props }: HTMLArkProps<'thead'>) {
	const { variant } = useTableContext();
	return (
		<ark.thead
			data-slot="table-header"
			className={cn(headerVariantClasses[variant], className)}
			{...props}
		/>
	);
}

function TableBody({ className, ...props }: HTMLArkProps<'tbody'>) {
	return <ark.tbody data-slot="table-body" className={cn('pt-table-body', className)} {...props} />;
}

function TableRow({ className, ...props }: HTMLArkProps<'tr'>) {
	const { variant } = useTableContext();
	return (
		<ark.tr
			data-slot="table-row"
			className={cn(rowVariantClasses[variant], className)}
			{...props}
		/>
	);
}

interface TableColumnHeaderCellProps extends HTMLArkProps<'th'> {
	align?: 'left' | 'center' | 'right';
}

function TableColumnHeaderCell({
	className,
	align = 'left',
	...props
}: TableColumnHeaderCellProps) {
	const { size } = useTableContext();
	const alignClass = {
		left: 'text-left',
		center: 'text-center',
		right: 'text-right'
	}[align];

	return (
		<ark.th
			data-slot="table-column-header"
			className={cn('pt-table-column-header', headerSizeClasses[size], alignClass, className)}
			{...props}
		/>
	);
}

interface TableCellProps extends HTMLArkProps<'td'> {
	align?: 'left' | 'center' | 'right';
}

function TableCell({ className, align = 'left', ...props }: TableCellProps) {
	const { size } = useTableContext();
	const alignClass = {
		left: 'text-left',
		center: 'text-center',
		right: 'text-right'
	}[align];

	return (
		<ark.td
			data-slot="table-cell"
			className={cn('pt-table-cell', cellSizeClasses[size], alignClass, className)}
			{...props}
		/>
	);
}

const Table = {
	Root: TableRoot,
	Header: TableHeader,
	Body: TableBody,
	Row: TableRow,
	ColumnHeaderCell: TableColumnHeaderCell,
	Cell: TableCell
};

export { Table, type TableRootProps };
